import { cloneDeep } from 'lodash';
import { EventbriteAPI } from '@eventbrite/api';
import { server } from '../config';
import { initializeMux } from '../mux';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NOT_REFERENCED_KEYS = [
  '_uid',
  'id',
  'uuid',
  'group_id',
  'viddy_video_id',
  'list_id',
];
const REFERENCE = '_reference';
const FOLDER_START_NAME = 'folder';
const FOLDER_UPDATE_NAME = '_updated';

const isUUID = value => UUID_REGEX.test(value);

const isRelation = (value, key) =>
  typeof value === 'string' &&
  isUUID(value) &&
  !NOT_REFERENCED_KEYS.includes(key);

const isReference = value => value.component?.endsWith(REFERENCE);

const isEventCard = value => value.component === 'events_page_card';

const isMuxVideo = value => value.component === 'mux_video';

const isFolder = key => key.startsWith(FOLDER_START_NAME);
const isUpdatedFolder = key => key.endsWith(FOLDER_UPDATE_NAME);

const createReference = value => {
  const componentName = value.component.split(REFERENCE)[0];
  const newContent = value[componentName]?.content || value[componentName];
  return { ...newContent };
};

const findMuxAsset = async url => {
  const Video = initializeMux();
  let asset;
  let page = 0;
  while (asset === undefined) {
    const assetListResponse = await Video.Video.Assets.list({ page });
    asset = assetListResponse.find(asset => asset.passthrough === url);
    if (assetListResponse.length === 0) {
      break;
    }
    page++;
  }
  return asset;
};

const findRelations = async (fullSlug, obj, fn, folderFn) => {
  for (let key in obj) {
    const value = obj[key];
    if (value && typeof value === 'object' && !isFolder(key)) {
      await findRelations(fullSlug, value, fn, folderFn);
      if (isReference(value)) {
        obj[key] = createReference(value);
      }
      if (isEventCard(value)) {
        const eventId = value.event_id;
        const eventbrite = new EventbriteAPI(server.EVENTBRITE_API_KEY);
        const event = await eventbrite.getEvent(eventId);
        value.event = event;
      }
      if (isMuxVideo(value)) {
        value.mux_asset = await findMuxAsset(value.video.filename);
      }
    } else if (isRelation(value, key)) {
      obj[key] = await fn(value);
      await findRelations(fullSlug, obj, fn, folderFn);
    } else if (isFolder(key) && !isUpdatedFolder(key)) {
      delete obj[key];
      obj[`${key}${FOLDER_UPDATE_NAME}`] = await folderFn(value);
    }
  }
};

export const resolveRelations = async (fullSlug, obj, fn, folderFn) => {
  const result = cloneDeep(obj);
  await findRelations(fullSlug, result, fn, folderFn);
  return result;
};
